import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { ConfigurationService, MapService, DynamicFormControlService, DynamicForm } from '@un-fao/lightforms-lib/lightforms-services';
import * as xml2js from 'xml2js';

@Component({
    selector: 'firms-map',
    templateUrl: './firms-map.component.html',
    styleUrl: './firms-map.component.css',
    standalone: false
})
export class FirmsMapComponent implements OnInit 
{
  @Input() collection: string;
  @Input() collectionForm: any;
  @Input() data: any;
  @Output() mapReady: EventEmitter<any> = new EventEmitter<any>();

  public config: any;
  public pageReady: boolean = false;

  colors: Array<string> = ["852D36", "A60314", "FF031C", "FA485B", "F58E98", "CC8914", "FAA616", "FAC002", "F0E92E"];

  mapInfo: any = {};
  layers: Array<any>;
  extent: any;

  constructor(
    public configService: ConfigurationService,
    public mapService: MapService,
    private dynamicFormControlService: DynamicFormControlService
  ) { }

  ngOnInit(): void 
  {
    this.extractPrimaryAreaInfo();

    if (this.mapInfo[this.data.document.objectSource.dataProvider.code].fao?.length > 0) {
      this.mapService.getFeature(this.configService.env.mapConfig.layers.fao.wfsUrl || this.configService.env.mapConfig.params.wfsUrl, this.configService.env.mapConfig.layers.fao.code, this.getCqlFilter("fao"), this.configService.env.mapConfig.layers.fao.filterName).subscribe(res => {
        const parser = new xml2js.Parser({
          strict: false,
          trim: true
        });
        parser.parseString(res, (err, result) => {
          this.extent = this.mapService.getExtentFromFeature(result);
          this.init();
        });

      }, error => {

      });
    }
    else {
      this.init();
    }
  }

  extractPrimaryAreaInfo() 
  {
    let isFao: boolean = false;

    this.mapInfo[this.data.document.objectSource.dataProvider.code] = {};

    let primaryArea: Array<any> = (this.collection === "resource" ? this.data.document.distribution?.primaryArea : this.data.document.fishingarea?.fisheryPrimaryAreaCodes);

    for (let i = 0; i < primaryArea?.length; i++) {
      let id = (primaryArea[i].value);  //primaryArea[i].figisId || 

      if (id.toLowerCase().startsWith("fao")) {
        this.fillInfo("fao", id);
        isFao = true;
      }
      else if (id.toLowerCase().startsWith("pac_tuna_rep")) {
        this.fillInfo("pacTunaRep", id);
      }
      else if (id.toLowerCase().startsWith("iccat_smu")) {
        this.fillInfo("iccatSmu", id);
      }
      else if (id.toLowerCase().startsWith("ices_fu")) {
        this.fillInfo("icesFu", id);
      }
      else if (id.toLowerCase().startsWith("ices_sa")) {
        this.fillInfo("icesSa", id);
      }
      else if (id.toLowerCase().startsWith("siofa")) {
        this.fillInfo("siofaSubarea", id);
      }
      else if (id.toLowerCase().startsWith("gfcm_sub_area")) {
        this.fillInfo("gfcmSubarea", id);
      }
      else if (id.toLowerCase().startsWith("eez")) {
        this.fillInfo("eez", id);
      }
      else if (id.toLowerCase().startsWith("lme")) {
        this.fillInfo("lme", id);
      }
      else if (id.toLowerCase().startsWith("rfb_comp")) {
        this.fillInfo("rfbComp", id);
      }
      else if (id.toLowerCase().startsWith("rep_area")) {
        this.fillInfo("repArea", id);
      }
      
      if (!isFao) {
        this.fillInfo("firms",id);
      }
    }
  }

  init() {
    this.config = {
      showLegends: true,
      constrainResolution: true,
      extent: this.extent,
      legend: {
        position: "bottom"
      }
    };

    this.layers = [];

    if (this.data.document.objectSource.dataProvider.code === 'CCAMLR') {
      this.config.projections = this.mapService.projections;
      this.config.defaultProjection = this.mapService.projections.south_polar;
    }

    this.initLayers();

    this.config.layers = this.layers;

    this.pageReady = true;
  }

  initLayers() 
  {
    this.addSpeciesLayers();

    if (this.mapInfo[this.data.document.objectSource.dataProvider.code].fao?.length > 0) {
      this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"fao",{
        cql_filter: this.getCqlFilter("fao")
      }));
    }

    if (this.mapInfo[this.data.document.objectSource.dataProvider.code].firms?.length > 0) {
      this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"firms",{
        cql_filter: this.getCqlFilter("firms")
      }));
    }
        
    this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"unContinents"));
    this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"marineAreas"));
    this.getFaoMajorAreasLayer();

    this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"limit200nm"));
  }

  addSpeciesLayers() 
  {
    /*
      STYLES FOR SPECIES_DIST CHANGE FOR EACH SPECIES ADDED (TO UNDERSTAND THE LOGIC)
    */
    let j = 0;

    for (let i = 0; i < this.data.document.species?.list?.length; i++) 
    {
      this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"species",{
        dynamicName: this.configService.env.mapConfig.layers.species.dynamicName?.replace("${alphaCode}",this.data.document.species.list[i].alphaCode),
        title: this.dynamicFormControlService.getFormProperty(this.data,this.collectionForm?.form,"layer.species", "label")?.replace("${species}",this.data.document.species.list[i].label),
        cql_filter: this.configService.env.mapConfig.layers.species.cql_filter?.replace("${alphaCode}",this.data.document.species.list[i].alphaCode),
        style: this.configService.env.mapConfig.layers.species.style?.replace("${color}",this.colors[j])
      }));

      j = j + 1;

      if (j === this.colors.length) {
        j = 0;
      }
    }
  }

  getFaoLayer() 
  {
    let majorAreasFilter: string = this.getCqlFilter("fao");
    let areasFilter: string = this.getCqlFilter("fao");
    let filter: string;

    if (majorAreasFilter > "" && areasFilter > "") {
      filter = majorAreasFilter + " OR " + areasFilter;
    } else {
      filter = areasFilter || majorAreasFilter;
    }

    if (filter) {
      this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"fao",{
        cql_filter: filter
      }));
    }
  }

  getFaoAreasLayer() 
  {
    let majorAreasFilter: string = this.getCqlFilter("faoMajorFishingAreas");
    let areasFilter: string = this.getCqlFilter("faoAreas");
    let filter: string;

    if (majorAreasFilter > "" && areasFilter > "") {
      filter = majorAreasFilter + " OR " + areasFilter;
    } else {
      filter = areasFilter || majorAreasFilter;
    }

    if (filter) {
      this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"faoAreas",{
        cql_filter: filter
      }));
    }
  }

  getFaoMajorAreasLayer() 
  {
/*     if (this.mapInfo[this.data.document.objectSource.dataProvider.code].majorareas && this.mapInfo[this.data.document.objectSource.dataProvider.code].majorareas?.length > 0) 
    {
      this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"faoMajorFishingAreas",{
        cql_filter: this.getCqlFilter("faoMajorFishingAreas")
      }));
    } */

    this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"faoMajorFishingAreasGlobal",{
      cql_filter: this.configService.env.mapConfig.layers["faoMajorFishingAreasGlobal"].cql_filter
    }));

    /* this.layers.push(this.mapService.getLayer(this.configService.env.mapConfig,this.data,this.collectionForm?.form,"faoMajorFishingAreasCodes")); */
  }

  fillInfo(type: string, value: string) {
    if (!this.mapInfo[this.data.document.objectSource.dataProvider.code][type]) {
      this.mapInfo[this.data.document.objectSource.dataProvider.code][type] = [];
    }

    if (!this.mapInfo[this.data.document.objectSource.dataProvider.code][type].includes(value)) {
      this.mapInfo[this.data.document.objectSource.dataProvider.code][type].push(value);
    }
  }

  getCqlFilter(type: string, name?: string) {
    let variable: string;

    if (name) {
      variable = name;
    }
    else {
      variable = this.configService.env.mapConfig.layers[type].filterName;
    }

    if (this.mapInfo[this.data.document.objectSource.dataProvider.code][type] && this.mapInfo[this.data.document.objectSource.dataProvider.code][type]?.length > 0) {
      let items = this.mapInfo[this.data.document.objectSource.dataProvider.code][type].map(e => {
        return variable + "='" + e + "'";
      });

      return items.join(" OR ");
    }
    else {
      return undefined;
    }
  }

  onMapReady(event: any) {
    this.mapReady.emit(event);
  }
}
